import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Rocket, Hammer, Layers } from "lucide-react";
import { Placeholder } from "@/components/Placeholder";
import { cn } from "@/lib/utils";

const ASSET_BASE = `${process.env.PUBLIC_URL || ""}/assets`;

/**
 * "In build right now" — Twasal Awasal card links OUT to the external
 * Twasal Awasal site when REACT_APP_TWASAL_URL is set. When the variable is
 * empty (default), no link and no clickable area is shown — an inert
 * placeholder link is worse than none at all.
 */
const TWASAL_URL = (process.env.REACT_APP_TWASAL_URL || "").trim();

function StatusPill({ status }) {
  const styles = {
    launching: "bg-wafa-primary text-white",
    building: "bg-wafa-deep/10 text-wafa-deep",
    coming: "bg-wafa-ink/10 text-wafa-ink/80",
  }[status];
  const label = { launching: "Ready for launch", building: "In build", coming: "Coming" }[status];
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider", styles)}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", status === "launching" ? "bg-white" : status === "building" ? "bg-wafa-deep" : "bg-wafa-ink/60")} />
      {label}
    </span>
  );
}

export function InBuildNow() {
  return (
    <section data-testid="in-build-now-section" className="bg-wafa-cream/40">
      <div className="mx-auto max-w-7xl px-4 py-20 lg:px-6 lg:py-28">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">Ready for launch</div>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            One ready to launch. More on the way.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-wafa-ink/80 lg:text-lg">
            We are a product company, and the bench is never empty. Twasal Awasal is ready to go live, and the next products are already in build.
          </p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {/* Card 1 — Twasal Awasal (flagship, double-width, highlight border) */}
          <article
            data-testid="in-build-card-twasal"
            className="relative flex flex-col overflow-hidden rounded-xl border-2 border-wafa-primary bg-white shadow-[0_28px_60px_-30px_rgba(88,175,88,0.45)] transition-transform duration-200 hover:-translate-y-1 lg:col-span-2 lg:flex-row lg:items-stretch"
          >
            <div className="absolute -top-px start-6 z-10 rounded-b-md bg-wafa-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              Flagship launch
            </div>
            <div className="p-6 sm:p-8 lg:w-1/2 lg:p-10">
              <div className="flex items-center justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-wafa-primary/10 text-wafa-primary">
                  <Rocket className="h-5 w-5" />
                </div>
                <StatusPill status="launching" />
              </div>
              <h3 className="mt-6 font-heading text-2xl font-semibold text-wafa-deep sm:text-3xl">
                Twasal Awasal
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-wafa-ink/80 lg:text-base">
                Our flagship launch. Twasal Awasal is a multi-service delivery application for Oman,
                built by Wafa Solutions in partnership with AlAwal Cargo Services. Customer app,
                driver app, live tracking, wallet, proof of delivery with photo and signature, and
                a business portal for shippers — eleven service types on one platform.
              </p>
              {TWASAL_URL ? (
                <a
                  href={TWASAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="in-build-twasal-cta"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-primary underline-offset-4 hover:underline"
                >
                  Visit the Twasal Awasal site <ExternalLink className="h-4 w-4" />
                </a>
              ) : null}
            </div>
            <div
              data-testid="in-build-twasal-image-wrap"
              className="relative flex items-end justify-center px-6 pt-6 lg:w-1/2 lg:px-0 lg:pt-0"
            >
              <picture className="block w-full max-w-md sm:max-w-lg lg:absolute lg:inset-x-0 lg:bottom-0 lg:max-w-none">
                <source srcSet={`${ASSET_BASE}/twasal-launch.webp`} type="image/webp" />
                <img
                  src={`${ASSET_BASE}/twasal-launch.png`}
                  alt="Twasal Awasal delivery app shown on a phone beside a branded delivery van"
                  width="876"
                  height="1062"
                  loading="lazy"
                  decoding="async"
                  data-testid="in-build-twasal-image"
                  className="block h-auto w-full object-contain object-bottom"
                />
              </picture>
            </div>
          </article>

          {/* Card 2 — Meyar */}
          <article
            data-testid="in-build-card-meyar"
            className="flex flex-col rounded-xl border border-wafa-deep/10 bg-white p-6 transition-transform duration-200 hover:-translate-y-1 sm:p-8"
          >
            <div className="flex items-center justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-wafa-deep/10 text-wafa-deep">
                <Hammer className="h-5 w-5" />
              </div>
              <StatusPill status="building" />
            </div>
            <h3 className="mt-6 font-heading text-2xl font-semibold text-wafa-deep">Meyar</h3>
            <p className="mt-4 text-sm leading-relaxed text-wafa-ink/75">
              Our governance, risk and compliance platform. The risk register and the AI assistant
              are working; controls, policy management and framework mapping are next.
            </p>
            <div className="mt-5">
              <picture>
                <source
                  type="image/webp"
                  srcSet={`${ASSET_BASE}/meyar-in-build-600.webp`}
                />
                <img
                  src={`${ASSET_BASE}/meyar-in-build.png`}
                  alt="Meyar Enterprise Overview dashboard showing open risks, risk heat map, risks-by-type doughnut, and compliance by framework including ISO 27001, Oman PDPL and CBO regulations"
                  width="600"
                  height="400"
                  loading="lazy"
                  decoding="async"
                  data-testid="in-build-meyar-image"
                  className="block aspect-[3/2] w-full rounded-md object-cover"
                />
              </picture>
            </div>
            <Link
              to="/meyar"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep underline-offset-4 hover:underline"
              data-testid="in-build-meyar-cta"
            >
              Explore Meyar <ArrowRight className="h-4 w-4" />
            </Link>
          </article>

          {/* Card 3 — More on the way */}
          <article
            data-testid="in-build-card-more"
            className="flex flex-col rounded-xl border border-wafa-deep/10 bg-white p-6 transition-transform duration-200 hover:-translate-y-1 sm:p-8 lg:col-span-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-wafa-ink/10 text-wafa-ink/70">
                  <Layers className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-wafa-deep">More on the way</h3>
              </div>
              <StatusPill status="coming" />
            </div>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-wafa-ink/75 lg:text-base">
              ERP and HRMS products for the Omani market, and further modules across our existing
              platforms. If you need something we have not built yet, that is often how a new
              product starts.
            </p>
            <Link
              to="/contact"
              data-testid="in-build-more-cta"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-primary underline-offset-4 hover:underline"
            >
              Talk to us about it <ArrowRight className="h-4 w-4" />
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
}
